import React, { useEffect, useState, useRef } from "react";
import Swal from "sweetalert2";
import { auth, db } from "../firebase";
import {
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
    getDoc,
    doc,
    Timestamp,
} from "firebase/firestore";
import moment from "moment";
import { useReactToPrint } from "react-to-print";

import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Button,
    TextField,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";

export default function Paymodewise() {
    const printRef = useRef();

    /* ================= STATE ================= */
    const [paymentModes, setPaymentModes] = useState([]);
    const [type, setType] = useState("");

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const [reportData, setReportData] = useState([]);

    const [total, setTotal] = useState(0);
    const [paid, setPaid] = useState(0);
    const [discount, setDiscount] = useState(0);
    const [remaining, setRemaining] = useState(0);
    const [propertyId, setPropertyId] = useState("");

    /* ================= DEFAULT DATE ================= */
    useEffect(() => {
        const today = new Date().toISOString().split("T")[0];
        setFromDate(today);
        setToDate(today);
    }, []);

    /* ================= AUTH → PROPERTY ================= */
    useEffect(() => {
        const unsub = auth.onAuthStateChanged(async (user) => {
            if (!user) return;

            const snap = await getDoc(doc(db, "authroles", user.uid));
            if (snap.exists()) {
                setPropertyId(snap.data().propertyId);
            }
        });

        return () => unsub();
    }, []);

    /* ================= FETCH PAYMENT MODES ================= */
    useEffect(() => {
        if (!propertyId) return;

        return onSnapshot(
            query(
                collection(db, "restopaymodes"),
                where("propertyId", "==", propertyId)
            ),
            (snap) => {
                const modes = snap.docs.map((d) => ({
                    id: d.id,
                    ...d.data(),
                }));
                setPaymentModes(modes);
                console.log(modes);


            }
        );
    }, [propertyId]);

    /* ================= PRINT ================= */
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
    });

    /* ================= FETCH REPORT ================= */
    const getReport = () => {
        if (!propertyId || !type) {
            Swal.fire("Error", "Select payment mode", "error");
            return;
        }

        const start = new Date(fromDate);
        start.setHours(0, 0, 0, 0);

        const end = new Date(toDate);
        end.setHours(23, 59, 59, 999);

        const q = query(
            collection(db, "orders"),
            where("propertyId", "==", propertyId),
            where("paymentmode", "==", type),
            where("createdAt", ">=", Timestamp.fromDate(start)),
            where("createdAt", "<=", Timestamp.fromDate(end)),
            orderBy("createdAt", "desc")
        );

        onSnapshot(q, async (snap) => {
            let t = 0,
                p = 0,
                d = 0,
                r = 0;

            const rows = [];

            for (const docSnap of snap.docs) {
                const data = docSnap.data();
                let customerName = "";

                if (data.customerid) {
                    const cSnap = await getDoc(doc(db, "restocustomers", data.customerid));
                    if (cSnap.exists()) customerName = cSnap.data().custname;
                }

                t += Number(data.totalamount || 0);
                p += Number(data.paid || 0);
                d += Number(data.discount || 0);
                r += Number(data.remaining || 0);

                rows.push({
                    id: docSnap.id,
                    ...data,
                    customerName,
                });
            }

            setReportData(rows);
            setTotal(t);
            setPaid(p);
            setDiscount(d);
            setRemaining(r);
        });
    };

    /* ================= UI ================= */
    return (
        <Box className=" p-3">
            <Card>
                <CardContent>

                    {/* FILTER CARD */}
                    <Typography variant="h5" fontWeight="bold" mb={2}>
                        Payment Mode Report
                    </Typography>
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Grid className="row" spacing={2}>
                                <Grid className="col-sm-3">
                                    <TextField
                                        select
                                        fullWidth size="small"
                                        label="Payment Mode"
                                        value={type}
                                        onChange={(e) => setType(e.target.value)}
                                    >
                                        {paymentModes.map((m) => (
                                            <MenuItem key={m.id} value={m.paymode}>
                                                {m.paymode}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>

                                <Grid className="col-sm-3">
                                    <TextField
                                        type="date"
                                        label="From Date"
                                        fullWidth size="small"
                                        InputLabelProps={{ shrink: true }}
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                    />
                                </Grid>

                                <Grid className="col-sm-3">
                                    <TextField
                                        type="date"
                                        label="To Date"
                                        fullWidth size="small"
                                        InputLabelProps={{ shrink: true }}
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                    />
                                </Grid>

                                <Grid className="col-sm-3" alignSelf="end">
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={getReport}
                                    >
                                        Get Report
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* REPORT TABLE */}
                    <Card>
                        <CardContent ref={printRef}>
                            <Typography align="center" variant="h6">
                                Paymode Wise Report
                            </Typography>
                            <Typography align="center" variant="body2">
                                {moment(fromDate).format("DD-MM-YYYY")} to{" "}
                                {moment(toDate).format("DD-MM-YYYY")}
                            </Typography>

                            <TableContainer component={Paper} sx={{ mt: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Sr</TableCell>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Customer</TableCell>
                                            <TableCell>Total</TableCell>
                                            <TableCell>Paid</TableCell>
                                            <TableCell>Discount</TableCell>
                                            <TableCell>Remaining</TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {reportData.map((row, i) => (
                                            <TableRow key={row.id}>
                                                <TableCell>{i + 1}</TableCell>
                                                <TableCell>
                                                    {moment(row.createdAt.toDate()).format("DD-MM-YYYY")}
                                                </TableCell>
                                                <TableCell>{row.customerName}</TableCell>
                                                <TableCell>{row.totalamount}</TableCell>
                                                <TableCell>{row.paid}</TableCell>
                                                <TableCell>{row.discount}</TableCell>
                                                <TableCell>{row.remaining}</TableCell>
                                            </TableRow>
                                        ))}

                                        <TableRow>
                                            <TableCell colSpan={3}><b>Total</b></TableCell>
                                            <TableCell><b>{total}</b></TableCell>
                                            <TableCell><b>{paid}</b></TableCell>
                                            <TableCell><b>{discount}</b></TableCell>
                                            <TableCell><b>{remaining}</b></TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>

                        <Box textAlign="center" p={2}>
                            <Button variant="outlined" onClick={handlePrint}>
                                Print
                            </Button>
                        </Box>
                    </Card>

                </CardContent>
            </Card>
        </Box>
    );
}
